// frontend/src/components/Samples/SizesModal.jsx

import React, { useState, useEffect } from 'react';
import { horzValuesAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSave } from 'react-icons/fi';
import '../Settings/Modal.css';
import './SizesModal.css';

const SizesModal = ({ sizeTable, onClose }) => {
  const { t } = useTranslation();
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingSize, setEditingSize] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    horzValue: '',
    sorting: 10,
    description: ''
  });

  useEffect(() => {
    loadSizes();
  }, [sizeTable]);

  const loadSizes = async () => {
    try {
      setLoading(true);
      const response = await horzValuesAPI.getAll(sizeTable.horz_group);
      
      if (response.data.success) {
        setSizes(response.data.data);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error loading sizes:', err);
      setError(t('messages.error_load', 'Failed to load sizes'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSize(null);
    setFormData({
      horzValue: '',
      sorting: (Math.max(...sizes.map(s => s.sorting ?? 0), 0) + 10),
      description: ''
    });
    setShowForm(true);
  };

  const handleEdit = (size) => {
    setEditingSize(size);
    setFormData({
      horzValue: size.horz_value,
      sorting: size.sorting,
      description: size.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (size) => {
    if (!window.confirm(t('messages.confirm_delete', `Delete size "${size.horz_value}"?`))) {
      return;
    }

    try {
      const response = await horzValuesAPI.delete(size.horz_value_id);
      if (response.data.success) {
        loadSizes();
      }
    } catch (err) {
      alert(err.response?.data?.error || t('messages.error_delete', 'Failed to delete'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      horzGroup: sizeTable.horz_group,
      horzValue: formData.horzValue,
      sorting: formData.sorting,
      description: formData.description
    };

    try {
      let response;
      if (editingSize) {
        response = await horzValuesAPI.update(editingSize.horz_value_id, data);
      } else {
        response = await horzValuesAPI.create(data);
      }

      if (response.data.success) {
        setShowForm(false);
        loadSizes();
      }
    } catch (err) {
      alert(err.response?.data?.error || t('messages.error_save', 'Failed to save'));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSize(null);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content sizes-modal-content">
        <div className="modal-header">
          <h3>
            {t('sizes.manage_for', 'Manage Sizes for')} {sizeTable.horz_group}
            {sizeTable.description && ` - ${sizeTable.description}`}
          </h3>
          <button type="button" onClick={onClose} className="modal-close">
            ×
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">{t('loading', 'Loading...')}</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <>
              {/* Sizes List */}
              {!showForm && (
                <>
                  <div className="sizes-header">
                    <h4>{t('sizes.current', 'Current Sizes')} ({sizes.length})</h4>
                    <button onClick={handleCreate} className="btn-primary btn-sm">
                      <FiPlus size={14} />
                      {t('sizes.add', 'Add Size')}
                    </button>
                  </div>

                  {sizes.length === 0 ? (
                    <div className="no-sizes">
                      <p>{t('sizes.none', 'No sizes defined yet. Click "Add Size" to create one.')}</p>
                    </div>
                  ) : (
                    <div className="sizes-list">
                      <table>
                        <thead>
                          <tr>
                            <th>{t('sizes.code', 'Size Code')}</th>
                            <th className="text-center">{t('sizes.sorting', 'Order')}</th>
                            <th>{t('sizes.description', 'Description')}</th>
                            <th style={{ width: '100px' }}>{t('actions', 'Actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sizes
                            .sort((a, b) => a.sorting - b.sorting)
                            .map(size => (
                              <tr key={size.horz_value_id}>
                                <td className="code-column">{size.horz_value}</td>
                                <td className="text-center">{size.sorting}</td>
                                <td>{size.description || '-'}</td>
                                <td>
                                  <div className="action-buttons">
                                    <button
                                      onClick={() => handleEdit(size)}
                                      className="btn-action btn-edit"
                                      title={t('edit', 'Edit')}
                                    >
                                      <FiEdit2 size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(size)}
                                      className="btn-action btn-delete"
                                      title={t('delete', 'Delete')}
                                    >
                                      <FiTrash2 size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* Size Form */}
              {showForm && (
                <div className="size-form">
                  <h4>{editingSize ? t('sizes.edit', 'Edit Size') : t('sizes.create', 'Create Size')}</h4>
                  
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label>{t('sizes.code', 'Size Code')} *</label>
                      <input
                        type="text"
                        value={formData.horzValue}
                        onChange={(e) => setFormData({ ...formData, horzValue: e.target.value })}
                        maxLength={20}
                        required
                        autoFocus
                        disabled={!!editingSize}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('sizes.sorting', 'Sort Order')} *</label>
                      <input
                        type="number"
                        value={formData.sorting}
                        onChange={(e) => setFormData({ ...formData, sorting: parseInt(e.target.value) })}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('sizes.description', 'Description')}</label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        maxLength={50}
                      />
                    </div>

                    <div className="form-actions">
                      <button type="button" onClick={handleCancel} className="btn-secondary">
                        <FiX size={16} />
                        {t('cancel', 'Cancel')}
                      </button>
                      <button type="submit" className="btn-primary">
                        <FiSave size={16} />
                        {t('save', 'Save')}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SizesModal;